import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, XCircle, RotateCcw, MessageSquare, FileText, Clock } from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { es } from 'date-fns/locale'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import StatusBadge from '@/components/shared/StatusBadge'
import type { Validation, ValidationStatus } from '@/types'

// =============================================================================
// ValidationQueue Component
// =============================================================================

export default function ValidationQueue() {
  const { canValidate } = useAuth()
  const queryClient     = useQueryClient()
  const [reviewId, setReviewId]     = useState<string | null>(null)
  const [reviewComment, setComment] = useState('')
  const [reviewAction, setAction]   = useState<'APROBADO' | 'RECHAZADO' | 'EN_REVISION' | null>(null)

  // ---------------------------------------------------------------------------
  // Fetch pending validations
  // ---------------------------------------------------------------------------
  const { data: validations, isLoading } = useQuery<Validation[]>({
    queryKey: ['validations', 'pending'],
    queryFn: async (): Promise<Validation[]> => {
      if (isDemoMode) {
        return [
          { id: 'v1', activity_instance_id: 'inst-0-1', proposed_by: 'demo-user-001', proposed_at: '2026-03-24T08:00:00Z', proposed_pct: 65, validated_pct: null, status: 'PENDIENTE', reviewer_id: null, reviewed_at: null, comment: null, updated_at: '2026-03-24T08:00:00Z', proposer: { full_name: 'Mecano Trackers SL', company: { name: 'Mecano Trackers SL' } }, activity_instance: { progress_pct: 60, scope_ref: 'Parc-03', family: { name: 'Montaje de tracker', discipline: 'Mecánica' }, parcel: { display_name: 'Parcela 3' } } },
          { id: 'v2', activity_instance_id: 'inst-1-2', proposed_by: 'demo-user-002', proposed_at: '2026-03-26T10:30:00Z', proposed_pct: 40, validated_pct: null, status: 'PENDIENTE', reviewer_id: null, reviewed_at: null, comment: null, updated_at: '2026-03-26T10:30:00Z', proposer: { full_name: 'Electra Obras SA', company: { name: 'Electra Obras SA' } }, activity_instance: { progress_pct: 35, scope_ref: 'Parc-06', family: { name: 'Conexionado BT', discipline: 'Eléctrica BT' }, parcel: { display_name: 'Parcela 6' } } },
          { id: 'v3', activity_instance_id: 'inst-2-3', proposed_by: 'demo-user-003', proposed_at: '2026-03-27T14:00:00Z', proposed_pct: 80, validated_pct: null, status: 'PENDIENTE', reviewer_id: null, reviewed_at: null, comment: null, updated_at: '2026-03-27T14:00:00Z', proposer: { full_name: 'Civil Navabuena UTE', company: { name: 'Civil Navabuena UTE' } }, activity_instance: { progress_pct: 75, scope_ref: 'Parc-11', family: { name: 'Zanjas MT', discipline: 'Eléctrica MT' }, parcel: { display_name: 'Parcela 11' } } },
        ] as unknown as Validation[]
      }
      const { data, error } = await supabase
        .from('validations')
        .select(`
          *,
          proposer:user_profiles!proposed_by(full_name, company:companies(name)),
          activity_instance:activity_instances(
            progress_pct, scope_ref,
            family:activity_families(name, discipline),
            parcel:parcels(display_name)
          )
        `)
        .eq('status', 'PENDIENTE')
        .order('proposed_at', { ascending: true })
        .limit(50)

      if (error) throw error
      return (data ?? []) as Validation[]
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // ---------------------------------------------------------------------------
  // Review mutation
  // ---------------------------------------------------------------------------
  const reviewMutation = useMutation({
    mutationFn: async ({
      validationId,
      status,
      comment,
      validatedPct,
    }: {
      validationId: string
      status:       ValidationStatus
      comment:      string
      validatedPct?: number
    }) => {
      if (isDemoMode) return  // no-op in demo
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('validations')
        .update({
          status,
          review_comment: comment || null,
          reviewed_by:    user.id,
          reviewed_at:    new Date().toISOString(),
          validated_pct:  validatedPct,
        })
        .eq('id', validationId)

      if (error) throw error

      // If approved, update the subactivity progress
      if (status === 'APROBADO') {
        const validation = validations?.find((v) => v.id === validationId)
        if (validation?.activity_instance_id && validation.template_id) {
          await supabase
            .from('subactivity_progress')
            .update({ validated_pct: validatedPct ?? validation.proposed_pct })
            .eq('activity_instance_id', validation.activity_instance_id)
            .eq('template_id', validation.template_id)
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validations'] })
      queryClient.invalidateQueries({ queryKey: ['activities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setReviewId(null)
      setComment('')
      setAction(null)
    },
  })

  const submitReview = (validation: Validation) => {
    if (!reviewAction) return
    reviewMutation.mutate({
      validationId: validation.id,
      status:       reviewAction,
      comment:      reviewComment,
      validatedPct: reviewAction === 'APROBADO' ? validation.proposed_pct : undefined,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!validations || validations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-7 h-7 text-green-500" />
        </div>
        <p className="text-sm font-semibold text-gray-700">Cola vacía</p>
        <p className="text-xs text-gray-400 mt-1">No hay validaciones pendientes de revisión</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {validations.map((validation) => {
        const instance  = validation.activity_instance as {
          scope_ref: string
          progress_pct: number
          family: { name: string; discipline: string }
          parcel: { display_name: string } | null
        } | null
        const proposer  = validation.proposer as {
          full_name: string
          company: { name: string }
        } | null
        const isOld     = new Date(validation.proposed_at) < new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        const isReviewing = reviewId === validation.id

        return (
          <div
            key={validation.id}
            className={`card p-4 transition-shadow hover:shadow-card-hover ${
              isOld ? 'border-l-4 border-l-orange-400' : ''
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-gray-900">
                    {instance?.family?.name ?? '—'}
                  </span>
                  {instance?.parcel && (
                    <span className="text-xs text-gray-500">·</span>
                  )}
                  <span className="text-xs text-gray-600">
                    {instance?.parcel?.display_name ?? instance?.scope_ref ?? 'Global'}
                  </span>
                  {isOld && (
                    <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">
                      &gt;3 días
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(validation.proposed_at), { addSuffix: true, locale: es })}
                  </span>
                  {proposer && (
                    <span>
                      {proposer.full_name}
                      {proposer.company?.name && ` · ${proposer.company.name}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Proposed progress */}
              <div className="flex flex-col items-end shrink-0 ml-3">
                <div className="text-2xl font-bold text-brand-600">
                  {validation.proposed_pct}
                  <span className="text-sm font-medium text-gray-400">%</span>
                </div>
                <span className="text-[10px] text-gray-400">propuesto</span>
              </div>
            </div>

            {/* Progress delta */}
            {instance && (
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                <span>Actual: <strong className="text-gray-700">{instance.progress_pct.toFixed(0)}%</strong></span>
                <span>→</span>
                <span>Propuesto: <strong className="text-brand-600">{validation.proposed_pct}%</strong></span>
                <span className={`font-medium ${
                  validation.proposed_pct > instance.progress_pct ? 'text-green-600' : 'text-red-600'
                }`}>
                  ({validation.proposed_pct > instance.progress_pct ? '+' : ''}
                  {(validation.proposed_pct - instance.progress_pct).toFixed(0)}%)
                </span>
              </div>
            )}

            {/* Documents attached */}
            {validation.documents_attached && validation.documents_attached.length > 0 && (
              <div className="flex items-center gap-1.5 mb-3">
                <FileText className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {validation.documents_attached.length} documento{validation.documents_attached.length !== 1 ? 's' : ''} adjunto{validation.documents_attached.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Review actions */}
            {canValidate && !isReviewing && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => { setReviewId(validation.id); setAction('APROBADO') }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Aprobar
                </button>
                <button
                  onClick={() => { setReviewId(validation.id); setAction('RECHAZADO') }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Rechazar
                </button>
                <button
                  onClick={() => { setReviewId(validation.id); setAction('EN_REVISION') }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Devolver
                </button>
              </div>
            )}

            {/* Inline review form */}
            {canValidate && isReviewing && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-medium text-gray-700">
                    {reviewAction === 'APROBADO'    ? 'Aprobar' :
                     reviewAction === 'RECHAZADO'   ? 'Rechazar' : 'Devolver para revisión'}
                  </span>
                </div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Comentario (opcional para aprobar, requerido para rechazar)"
                  rows={2}
                  className="input-base text-xs resize-none mb-2"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => submitReview(validation)}
                    disabled={reviewMutation.isPending || (reviewAction === 'RECHAZADO' && !reviewComment.trim())}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                      reviewAction === 'APROBADO'  ? 'bg-green-600 text-white hover:bg-green-700' :
                      reviewAction === 'RECHAZADO' ? 'bg-red-600 text-white hover:bg-red-700' :
                      'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {reviewMutation.isPending ? (
                      <div className="w-3.5 h-3.5 border border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    Confirmar
                  </button>
                  <button
                    onClick={() => { setReviewId(null); setComment(''); setAction(null) }}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
